import { ChangeDetectorRef, Injectable, inject } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ISelect } from '@tecalisdev/infinity';
import { OPERATION_TYPE } from '../../operations/models/operations.models';
import { IMasterCompanyElement } from '../../shared/models/master.models';
import { LoadingService } from '../../shared/services/loading.service';
import { MasterService } from '../../shared/services/master.service';
import { SnackbarService } from '../../shared/services/snackbar.service';
import { maskApiKey } from '../../core/utils/utils';
import { isSpainCompanyCountry } from '../utils/company-country.util';
import { ApikeyService } from '../../settings/pages/apikeys/services/apikey.service';
import { IApikeyListBody, IApikeyListResponse } from '../../settings/pages/apikeys/models/apikeys.models';
import { CompaniesService } from '../../settings/pages/companies/services/companies.service';
import { ICompaniesDetailResponse } from '../../settings/pages/companies/models/companies.models';
import { DesignService } from '../../settings/pages/design/services/design.service';
import { IDesignListBody, IDesignListResponse } from '../../settings/pages/design/models/design.models';
import { LegalTermsService } from '../../settings/pages/legal-terms/services/legal-terms.service';
import {
	ILegalTermsListBody,
	ILegalTermsListResponse,
} from '../../settings/pages/legal-terms/models/legal-terms.models';
import { AuthChannel } from '../models/workflows.models';
import { WorkflowFormPayloadService } from './workflow-form-payload.service';

/**
 * Host para opciones de compañía del editor (NIF-845).
 */
export interface WorkflowFormCompanyOptionsHost {
	form: FormGroup;
	id: string | undefined;
	company?: IMasterCompanyElement;
	apikeysOptions: ISelect[];
	themeOptions: ISelect[];
	policyChecksOptions: ISelect[];
	selectedPolicyCheckControl: FormControl;
	showCreateApikeyForm: boolean;
	applyingWorkflowDetail: boolean;
	resolvingCompanyCountryUuid: string | null;
	get checksArray(): FormArray;
	ensureDefaultValidationManualErrorsSelection(): void;
	cdr: ChangeDetectorRef;
}

/**
 * Opciones ligadas a compañía: apikeys, theme, policy checks, Schengen, validación (NIF-845).
 *
 * Abrir cuando: `updateOptionsByCompany`, país compañía, 2FA channel.
 * Mapa: `../WORKFLOWS_EDITOR.md`.
 */
@Injectable({
	providedIn: 'root',
})
export class WorkflowFormCompanyOptionsService {
	private readonly router = inject(Router);
	private readonly loading = inject(LoadingService);
	private readonly snackbarService = inject(SnackbarService);
	private readonly trans = inject(TranslateService);
	private readonly masterService = inject(MasterService);
	private readonly apikeyService = inject(ApikeyService);
	private readonly companiesService = inject(CompaniesService);
	private readonly designService = inject(DesignService);
	private readonly legalTermsService = inject(LegalTermsService);
	private readonly payloadService = inject(WorkflowFormPayloadService);

	public closeApikeyForm(host: WorkflowFormCompanyOptionsHost, reload: boolean): void {
		if (reload) {
			this.updateApikeysOptions(host);
		}
		host.showCreateApikeyForm = false;
	}

	public createTheme(): void {
		this.router.navigate(['/settings/design']);
	}

	public updatePolicyChecksOptions(host: WorkflowFormCompanyOptionsHost): void {
		const company = host.form.get('general.company').value?.id ?? host.form.get('general.company').value;
		if (!company) {
			return;
		}
		this.legalTermsService.list({ company_uuid: company }).subscribe({
			next: (response: ILegalTermsListResponse) => {
				host.policyChecksOptions = response.data
					.filter((policyCheck: ILegalTermsListBody) => {
						return !host.checksArray.controls.some((control) => control.value.uuid === policyCheck.uuid);
					})
					.map((policyCheck: ILegalTermsListBody) => ({
						id: policyCheck.uuid,
						label: policyCheck.custom_id,
						value: policyCheck,
					}));
				this.loading.hide();
			},
			error: () => {
				host.policyChecksOptions = [];
				this.loading.hide();
			},
		});
	}

	public addPolicyCheck(host: WorkflowFormCompanyOptionsHost, policyCheckUuid: string | unknown): void {
		if (!policyCheckUuid) {
			return;
		}

		const uuid =
			typeof policyCheckUuid === 'string'
				? policyCheckUuid
				: (policyCheckUuid as { id?: string })?.id || policyCheckUuid;

		const existingCheck = host.checksArray.controls.find((control) => control.value.uuid === uuid);
		if (existingCheck) {
			this.snackbarService.show(this.trans.instant('WORKFLOWS.FORM.OPERATIONS.POLICY_CHECK_ALREADY_ADDED'));
			host.selectedPolicyCheckControl.setValue(null);
			return;
		}

		const selectedPolicyCheck = host.policyChecksOptions.find((pc: ISelect) => pc.id === uuid);
		if (selectedPolicyCheck?.value) {
			const checks = new FormGroup({
				custom_id: new FormControl(selectedPolicyCheck.value.custom_id),
				html: new FormControl(selectedPolicyCheck.value.html),
				required: new FormControl(selectedPolicyCheck.value.required || false),
				uuid: new FormControl(selectedPolicyCheck.value.uuid),
			});
			(host.form.get(`operations.${OPERATION_TYPE.HOME}.policy_checks`) as FormArray).push(checks);
			host.policyChecksOptions = host.policyChecksOptions.filter((pc: ISelect) => pc.id !== uuid);
			host.selectedPolicyCheckControl.setValue(null);
		}
	}

	public ensureTwoFactorWorkflowControl(form: FormGroup): void {
		this.payloadService.ensureTwoFactorWorkflowControl(form);
	}

	public isTwoFactorWorkflowEnabled(form: FormGroup): boolean {
		return this.getSelectedTwoFactorWorkflowChannel(form) !== 'none';
	}

	public getSelectedTwoFactorWorkflowChannel(form: FormGroup): AuthChannel {
		return this.payloadService.getSelectedTwoFactorWorkflowChannel(form);
	}

	public updateOptionsByCompany(host: WorkflowFormCompanyOptionsHost): void {
		this.updateApikeysOptions(host);
		this.updateThemeOptions(host);
		this.updatePolicyChecksOptions(host);
		this.updateCompanySelected(host);
	}

	public updateCompanySelected(host: WorkflowFormCompanyOptionsHost): void {
		const company_uuid = host.form.get('general.company').value?.id ?? host.form.get('general.company').value;
		host.company = this.masterService.master()?.companies?.find((c) => c.uuid === company_uuid);
		this.applyWorkflowValidationForCompany(host);
		this.ensureCompanyCountryResolved(host, typeof company_uuid === 'string' ? company_uuid : undefined);
	}

	public ensureCompanyCountryResolved(host: WorkflowFormCompanyOptionsHost, companyUuid?: string): void {
		if (!companyUuid) {
			host.resolvingCompanyCountryUuid = null;
			this.syncSchengenUnlimitedForCompany(host);
			return;
		}

		const hasCountry = Boolean(host.company?.country);
		const hasManualValidation = typeof host.company?.manual_validation === 'boolean';
		if (hasCountry && hasManualValidation) {
			host.resolvingCompanyCountryUuid = null;
			this.syncSchengenUnlimitedForCompany(host);
			return;
		}
		if (host.resolvingCompanyCountryUuid === companyUuid) {
			return;
		}
		host.resolvingCompanyCountryUuid = companyUuid;
		this.companiesService.detail(companyUuid).subscribe({
			next: (response: ICompaniesDetailResponse) => {
				if (host.resolvingCompanyCountryUuid !== companyUuid) {
					return;
				}
				const country = response?.data?.country;
				if (host.company?.uuid === companyUuid) {
					host.company = {
						...host.company,
						country,
						manual_validation: response?.data?.manual_validation,
					};
				} else if (!host.company) {
					host.company = {
						uuid: companyUuid,
						name: response?.data?.name ?? '',
						created_at: response?.data?.created_at ?? '',
						updated_at: response?.data?.updated_at ?? '',
						country,
						manual_validation: response?.data?.manual_validation,
					};
				}
				this.syncSchengenUnlimitedForCompany(host);
				this.applyWorkflowValidationForCompany(host);
				host.cdr.detectChanges();
			},
			error: () => {
				if (host.resolvingCompanyCountryUuid === companyUuid) {
					host.resolvingCompanyCountryUuid = null;
					this.syncSchengenUnlimitedForCompany(host);
				}
			},
			complete: () => {
				if (host.resolvingCompanyCountryUuid === companyUuid) {
					host.resolvingCompanyCountryUuid = null;
				}
			},
		});
	}

	public syncSchengenUnlimitedForCompany(host: WorkflowFormCompanyOptionsHost): void {
		if (host.applyingWorkflowDetail) {
			return;
		}
		const country = host.company?.country;
		if (typeof country === 'string' && country.length > 0 && !isSpainCompanyCountry(country)) {
			host.form
				.get(`operations.${OPERATION_TYPE.OCR}.is_schengen_unlimited`)
				?.patchValue(false, { emitEvent: false });
		}
	}

	public applyWorkflowValidationForCompany(host: WorkflowFormCompanyOptionsHost): void {
		if (host.company?.manual_validation === true) {
			return;
		}

		host.form.get('general.validation')?.patchValue('auto', { emitEvent: false });
		host.form.get('general.validation_email')?.patchValue('', { emitEvent: false });
		host.form.get('general.validation_manual_type')?.patchValue(['approved'], { emitEvent: false });
		host.form.get('general.validation_manual_errors')?.patchValue([], { emitEvent: false });
		host.ensureDefaultValidationManualErrorsSelection();
	}

	public updateApikeysOptions(host: WorkflowFormCompanyOptionsHost): void {
		host.form.get('general.api_key').disable();
		host.form.get('general.api_key').patchValue('');
		this.apikeyService
			.list({
				unpaginated: true,
				enabled: true,
				company_uuid: host.form.get('general.company').value?.id ?? host.form.get('general.company').value,
			})
			.subscribe({
				next: (response: IApikeyListResponse) => {
					host.form.get('general.api_key').enable();
					host.apikeysOptions = response.data.map((apikey: IApikeyListBody) => ({
						id: apikey.key,
						label: `${apikey.alias} (${maskApiKey(apikey.key)})`,
						value: apikey.uuid,
					}));
					if (host.id) {
						host.form
							.get('general.api_key')
							.patchValue(
								host.apikeysOptions.find(
									(opt: ISelect) => opt.id === host.form.get('general.api_key').value,
								),
							);
						host.form.get('general.api_key').updateValueAndValidity();
					}
				},
				error: (_error: HttpErrorResponse) => {
					host.form.get('general.api_key').enable();
					host.form.get('general.api_key').setErrors({ request: true });
					host.form.get('general.api_key').markAsTouched();
					this.loading.hide();
				},
				complete: () => {
					this.loading.hide();
				},
			});
	}

	public updateThemeOptions(host: WorkflowFormCompanyOptionsHost): void {
		host.form.get('general.theme').disable();
		host.form.get('general.theme').patchValue('');
		this.designService
			.list({
				unpaginated: true,
				enabled: true,
				company_uuid: host.form.get('general.company').value?.id ?? host.form.get('general.company').value,
			})
			.subscribe({
				next: (response: IDesignListResponse) => {
					host.form.get('general.theme').enable();
					host.themeOptions = response.data.map((design: IDesignListBody) => ({
						id: design.uuid,
						label: design.title,
						value: design,
					}));
					if (host.id) {
						host.form
							.get('general.theme')
							.patchValue(
								host.themeOptions.find(
									(opt: ISelect) => opt.id === host.form.get('general.theme').value,
								),
							);
						host.form.get('general.theme').updateValueAndValidity();
					}
				},
				error: (_error: HttpErrorResponse) => {
					host.form.get('general.theme').enable();
					host.form.get('general.theme').setErrors({ request: true });
					host.form.get('general.theme').markAsTouched();
					this.loading.hide();
				},
				complete: () => {
					this.loading.hide();
				},
			});
	}
}
